import { useState } from "react"

import { useCurrentOverlay } from "shared/context/overlay/OverlayIDContext"
import { useEvent } from "shared/lib/useEvent"
import { ApiResponse } from "shared/types/ApiResponse"
import { ApolloFetchPolicy } from "shared/types/ApolloFetchPolicy"
import { FilePreview } from "shared/types/File"

import { useAttachmentEntitiesQuery } from "entities/attachments/api/useAttachmentEntitiesQuery"
import { useProjectAttachmentUpload } from "entities/attachments/api/useProjectAttachmentUpload"
import { AttachmentFragment } from "entities/attachments/graphql/fragments.generated"
import { LinkedAttachmentEntity } from "entities/attachments/ui/linked-attachments/types/LinkedAttachmentEntity"

import { ReplaceAttachmentOverlayModel } from "../ReplaceAttachmentOverlay"
import { ReplaceAttachmentOverlayConnectorProps } from "../ReplaceAttachmentOverlayConnector"

export function useReplaceAttachmentOverlayModel({
  payload
}: ReplaceAttachmentOverlayConnectorProps): ReplaceAttachmentOverlayModel {
  const { closeOverlay } = useCurrentOverlay()
  const [newFile, setNewFile] = useState<AttachmentFragment | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const { replaceAttachment } = useProjectAttachmentUpload()
  const { data } = useAttachmentEntitiesQuery({
    id: payload.attachment.id,
    fetchPolicy: ApolloFetchPolicy.NETWORK_ONLY
  })

  const mockReplaceAttachment = useEvent(
    async (file: File): Promise<ApiResponse<AttachmentFragment>> => {
      return replaceAttachment(payload.attachment, file, payload.projectID)
    }
  )

  const handleUpload = useEvent(
    async ({ file }: FilePreview): Promise<ApiResponse<unknown>> => {
      setIsLoading(true)
      const response = await mockReplaceAttachment(file)
      setIsLoading(false)

      if (response.data) {
        setNewFile(response.data)
      }

      return response
    }
  )

  const handleConfirm = useEvent(async () => {
    closeOverlay()
  })

  return {
    payload,
    linkedEntities: data?.attachmentEntities.entities as
      | LinkedAttachmentEntity[]
      | undefined,
    newFile,
    isLoading,
    onUpload: handleUpload,
    onConfirm: handleConfirm,
    onClose: closeOverlay
  }
}
